/**
 * Base-path builders for the in-app chat surface. Four pure string builders
 * over one boolean: the flag crosses as an argument and the decision lives here.
 */

/** Evidence chat home — the pre-Work surface, and the fallback. */
export const LEGACY_CHAT_HOME_PATH = '/scribe/chat';

/** Work chat home, reached when the Projects flag is on. */
export const WORK_CHAT_HOME_PATH = '/scribe/chat/projects';

export type WorkHomeObjectKind = 'patient' | 'visit';

export interface ObjectWorkspaceRoute {
  kind: WorkHomeObjectKind;
  id: string;
  /** Session used to restore a patient identity without putting PHI in the URL. */
  sourceSessionId?: string;
}

type WorkHomeObjectQuery = Record<string, string | string[] | undefined>;

const readSingleQueryValue = (value: string | string[] | undefined): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/** Session id kept on the URL — patient-only, and never when it equals the object id. */
const effectiveSourceSessionId = ({
  kind,
  id,
  sourceSessionId,
}: ObjectWorkspaceRoute): string | undefined =>
  kind === 'patient' && sourceSessionId && sourceSessionId !== id
    ? sourceSessionId
    : undefined;

/** Canonical URL for a patient- or visit-scoped Home workspace. */
export const getWorkHomeObjectHref = ({
  kind,
  id,
  sourceSessionId,
}: ObjectWorkspaceRoute): string => {
  const params = new URLSearchParams({ objectKind: kind, objectId: id });
  const sessionId = effectiveSourceSessionId({ kind, id, sourceSessionId });
  if (sessionId) params.set('sourceSessionId', sessionId);
  return `${WORK_CHAT_HOME_PATH}?${params.toString()}`;
};

/** Read the root object from the settled Home route; presentation stays local. */
export const parseWorkHomeObjectRoute = (
  query: WorkHomeObjectQuery
): ObjectWorkspaceRoute | null => {
  const kind = readSingleQueryValue(query.objectKind);
  const id = readSingleQueryValue(query.objectId);
  if ((kind !== 'patient' && kind !== 'visit') || !id) return null;

  const sourceSessionId =
    kind === 'patient' ? readSingleQueryValue(query.sourceSessionId) : null;
  return {
    kind,
    id,
    ...(sourceSessionId ? { sourceSessionId } : {}),
  };
};

const stripLocalePrefix = (pathname: string, locales: readonly string[]): string => {
  const locale = locales.find(
    (candidate) => candidate !== 'default' && pathname.startsWith(`/${candidate}/`)
  );
  return locale ? pathname.slice(locale.length + 1) : pathname;
};

/**
 * Whether a destination URL leaves the currently mounted object workspace.
 * Used by the discard guard's `shouldHoldNavigation` override.
 */
export const shouldHoldObjectWorkspaceNavigation = (
  url: string,
  current: ObjectWorkspaceRoute,
  locales: readonly string[] = []
): boolean => {
  const targetUrl = new URL(url, 'https://heidi.local');
  const targetPath = stripLocalePrefix(targetUrl.pathname, locales).replace(/\/$/, '');
  if (targetPath !== WORK_CHAT_HOME_PATH) return true;

  const target = parseWorkHomeObjectRoute({
    objectKind: targetUrl.searchParams.get('objectKind') ?? undefined,
    objectId: targetUrl.searchParams.get('objectId') ?? undefined,
    sourceSessionId: targetUrl.searchParams.get('sourceSessionId') ?? undefined,
  });
  return (
    !target ||
    target.kind !== current.kind ||
    target.id !== current.id ||
    effectiveSourceSessionId(target) !== effectiveSourceSessionId(current)
  );
};

export const getBaseChatPath = (canUseWorkHome: boolean): string =>
  canUseWorkHome ? WORK_CHAT_HOME_PATH : LEGACY_CHAT_HOME_PATH;

/** Leading-slash-free segment for Tauri deep-link concatenation. */
export const getBaseChatPathSegment = (canUseWorkHome: boolean): string =>
  canUseWorkHome ? 'scribe/chat/projects' : 'scribe/chat';

/**
 * Work-home Evidence mode hands off to classic Evidence via
 * ``/scribe/chat?query=``. Pre-uploaded attachment IDs ride along as a
 * comma-separated ``attachmentIds`` query param so the first completion
 * can resolve them (DSK-1000). Filenames stay off the URL — they can
 * contain PHI. Empty ``text`` omits ``query`` so a sessionStorage handoff
 * (linked context / clinical wire prompts) can land without URL leakage.
 *
 * Lives next to ``LEGACY_CHAT_HOME_PATH`` so Chat files name this door
 * instead of a ``src/utils`` module (K2).
 */
export const EVIDENCE_CHAT_ATTACHMENT_IDS_PARAM = 'attachmentIds';

export function buildEvidenceChatRedirect(
  text: string,
  attachmentIds?: readonly string[]
): {
  pathname: typeof LEGACY_CHAT_HOME_PATH;
  query: { query?: string; attachmentIds?: string };
} {
  const trimmed = text.trim();
  return {
    pathname: LEGACY_CHAT_HOME_PATH,
    query: {
      ...(trimmed ? { query: trimmed } : {}),
      ...(attachmentIds && attachmentIds.length > 0
        ? { [EVIDENCE_CHAT_ATTACHMENT_IDS_PARAM]: attachmentIds.join(',') }
        : {}),
    },
  };
}

export function parseAttachmentIdsQuery(
  value: string | string[] | undefined
): string[] | undefined {
  if (value == null || value === '') return undefined;
  const ids = (Array.isArray(value) ? value : [value])
    .flatMap((part) => part.split(','))
    .map((id) => id.trim())
    .filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}
